import { useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import { Client, Activity } from '../../../pure-js/types/GrooverTypes'
import { buildActivityUrl } from '../../../pure-js/libs/SeoHelper'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { getActivity, getClient, getClientBySlug } from '../libs/DBApiHandler'
import { ActivityDetailContent } from '../components/ActivityDetailContent'
import useAppState from '../hooks/useAppState'
import { isSupportedLanguage, persistLanguageToLocalStateStorage } from '../libs/languagePersistence'
import { useCustomer } from '../context/CustomerContext'
import { getGrooverText } from '../libs/GrooverTextRepository'

/**
 * Strip HTML tags from a string for meta description
 */
const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '').trim()
}

/**
 * Activity detail page - handles both URL patterns:
 * - /activity/:activityId (legacy route)
 * - /:clientSlug/activity/:activitySlug (SEO-friendly route)
 */
export const ActivityDetailPage = () => {
  // Support both route patterns
  const { activityId, clientSlug, activitySlug } = useParams<{
    activityId?: string
    clientSlug?: string
    activitySlug?: string
  }>()

  // Determine which route pattern we're using
  const isSeoRoute = !!clientSlug && !!activitySlug
  const effectiveActivityId = isSeoRoute ? activitySlug : activityId

  const [searchParams] = useSearchParams()
  const { state } = useAppState()
  const { setCustomerId } = useCustomer()
  const [activity, setActivity] = useState<Activity | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorTextKey, setErrorTextKey] = useState<string | null>(null)

  useEffect(() => {
    const lang = searchParams.get('lang')
    if (!isSupportedLanguage(lang)) return
    persistLanguageToLocalStateStorage(lang)
  }, [searchParams])

  useEffect(() => {
    let isMounted = true

    const fetchActivity = async () => {
      const startTime = performance.now()

      try {
        if (!isMounted) return
        setIsLoading(true)

        // SEO route: /:clientSlug/activity/:activitySlug
        if (isSeoRoute) {
          if (!clientSlug || !activitySlug) {
            if (isMounted) {
              setErrorTextKey('userWeb.activityDetail.error.activityIdMissing')
              setIsLoading(false)
            }
            return
          }

          // Step 1: Resolve client by slug
          console.log('🔄 Starting to fetch client by slug...')
          const clientStart = performance.now()
          const clientData = await getClientBySlug(clientSlug, () => isMounted)
          console.log(`✅ Client fetched in ${performance.now() - clientStart}ms`)

          if (!isMounted) return

          if (!clientData) {
            setErrorTextKey('userWeb.activityDetail.error.clientNotFound')
            setIsLoading(false)
            return
          }

          setClient(clientData)

          // Step 2: Fetch activity by seoLink scoped to this client
          console.log('🔄 Starting to fetch activity by seoLink...')
          const activityStart = performance.now()
          const activityData = await getActivity(activitySlug, () => isMounted, clientData.id)
          console.log(`✅ Activity fetched in ${performance.now() - activityStart}ms`)

          if (!isMounted) return

          if (activityData) {
            setActivity(activityData)
            setErrorTextKey(null)
          } else {
            setErrorTextKey('userWeb.activityDetail.error.activityNotFound')
          }
        } else {
          // Legacy route: /activity/:activityId
          if (!activityId) {
            if (isMounted) {
              setErrorTextKey('userWeb.activityDetail.error.activityIdMissing')
              setIsLoading(false)
            }
            return
          }

          console.log('🔄 Starting to fetch activity...')
          const activityStart = performance.now()
          const activityData = await getActivity(activityId)
          console.log(`✅ Activity fetched in ${performance.now() - activityStart}ms`)

          if (!isMounted) return

          if (activityData) {
            setActivity(activityData)
            setErrorTextKey(null)

            if (activityData.clientId) {
              console.log('🔄 Starting to fetch client...')
              const clientStart = performance.now()
              const clientData = await getClient(activityData.clientId)
              console.log(`✅ Client fetched in ${performance.now() - clientStart}ms`)

              if (!isMounted) return

              if (clientData) {
                setClient(clientData)
                setErrorTextKey(null)
              } else {
                setErrorTextKey('userWeb.activityDetail.error.clientNotFound')
              }
            }
          } else {
            setErrorTextKey('userWeb.activityDetail.error.activityNotFound')
          }
        }
      } catch (err) {
        if (isMounted) {
          setErrorTextKey('userWeb.activityDetail.error.fetchFailed')
          console.error(err)
        }
      } finally {
        if (isMounted) {
          const totalTime = performance.now() - startTime
          console.log(`🎯 Total fetch time: ${totalTime}ms`)
          setTimeout(() => {
            if (isMounted) {
              setIsLoading(false)
            }
          }, 100)
        }
      }
    }

    fetchActivity()

    return () => {
      isMounted = false
    }
  }, [activityId, clientSlug, activitySlug, isSeoRoute])

  // Customer-specific translations: keep customerId in sync with the loaded client.
  useEffect(() => {
    setCustomerId(null)
  }, [effectiveActivityId, setCustomerId])

  useEffect(() => {
    if (client?.id) {
      setCustomerId(client.id)
    }
  }, [client?.id, setCustomerId])

  // Generate SEO meta content - use activity.seo overrides if available
  const seo = activity?.seo
  const seoTitle = seo?.title || (activity ? `${activity.name} - ${client?.name || 'Coursely'}` : 'Loading...')
  const seoDescriptionRaw =
    seo?.description || (activity?.description ? stripHtml(activity.description).slice(0, 160) : '')
  const seoDescription =
    seoDescriptionRaw || getGrooverText('userWeb.activityDetail.ogDefaultDescription', state?.selectedLanguage || 'sv')
  const seoKeywords = seo?.keywords?.join(', ') || ''
  const canonicalUrl =
    clientSlug && activity?.seoLink
      ? `https://${window.location.hostname}${buildRoute(RoutePath.ACTIVITY_DETAIL_SEO, { clientSlug, activitySlug: activity.seoLink })}`
      : buildActivityUrl(window.location.hostname, activity?.id || effectiveActivityId || '')

  // og:image fallback: bannerUrl → seo.image → default
  const ogImage = activity?.bannerUrl || activity?.seo?.image || `${window.location.origin}/og-activity-default.png`

  return (
    <>
      {/* SEO Meta Tags - uses activity.seo overrides when available */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {seoKeywords && <meta name="keywords" content={seoKeywords} />}
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <ActivityDetailContent
        activity={activity}
        client={client}
        isLoading={isLoading}
        errorTextKey={errorTextKey}
        activityId={effectiveActivityId}
      />
    </>
  )
}
